// src/screens/EditBlogPage.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  FileText, Link2, AlignLeft, Type,
  CheckCircle2, AlertCircle, Eye, EyeOff,
  Save, Send, Image, Tag, ArrowLeft
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import Logo from "../../img/logo.png";
import "./EditBlogPage.css";
import API_BASE from "../../config/api";

const EditBlogPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [activeSection, setActiveSection] = useState("Content");
  const [isLoadingPost, setIsLoadingPost] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: "",
    media_url: "",
    is_published: false,
  });

  const navItems = [
    { id: "Content", icon: FileText  },
    { id: "Media",   icon: Image     },
    { id: "Excerpt", icon: AlignLeft },
    { id: "Publish", icon: Send      },
  ];

  // ✅ Prefill form from API
  useEffect(() => {
    const fetchPost = async () => {
      setIsLoadingPost(true);
      setFetchError("");
      try {
        const token = localStorage.getItem("access_token"); // ✅ fixed
        const { data } = await axios.get(`${API_BASE}/blog/${id}`, {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        setFormData({
          title:        data.title        || "",
          content:      data.content      || "",
          excerpt:      data.excerpt === "string" ? "" : (data.excerpt || ""),
          media_url:    data.media_url    || "",
          is_published: data.is_published ?? false,
        });
      } catch (err) {
        setFetchError(err.response?.status === 404 ? "Post not found." : "Failed to load post.");
      } finally {
        setIsLoadingPost(false);
      }
    };
    if (id) fetchPost();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ✅ Submit with access_token
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token"); // ✅ fixed
      await axios.put(
        `${API_BASE}/blog/${id}`,
        {
          title:        formData.title,
          content:      formData.content,
          excerpt:      formData.excerpt,
          media_url:    formData.media_url,
          is_published: formData.is_published,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccess(true);
      setTimeout(() => navigate("/blog/list"), 1800);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Something went wrong."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const renderContent = () => {
    switch (activeSection) {

      case "Content":
        return (
          <div className="eb-animate">

            {/* Title */}
            <div className="eb-field">
              <label className="eb-label">
                <Type size={14} className="eb-label-icon" />
                Post Title <span className="eb-required">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter a compelling blog title..."
                className="eb-input"
                required
              />
            </div>

            {/* Content */}
            <div className="eb-field">
              <label className="eb-label">
                <FileText size={14} className="eb-label-icon" />
                Full Content <span className="eb-required">*</span>
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Write your full blog content here..."
                className="eb-textarea"
                rows={13}
                required
              />
              <span className="eb-char-count">{formData.content.length} characters</span>
            </div>

            {/* ✅ is_published inline toggle */}
            <div className="eb-field">
              <label className="eb-label">
                <Eye size={14} className="eb-label-icon" />
                Publication Status
              </label>
              <div className="eb-published-row">
                <div
                  className={`eb-toggle ${formData.is_published ? "eb-toggle-on" : ""}`}
                  onClick={() => setFormData((p) => ({ ...p, is_published: !p.is_published }))}
                >
                  <div className="eb-toggle-thumb" />
                </div>
                <div className="eb-published-info">
                  <span className={`eb-published-badge ${formData.is_published ? "published" : "draft"}`}>
                    {formData.is_published
                      ? <><Eye size={12} /> Published</>
                      : <><EyeOff size={12} /> Draft</>}
                  </span>
                  <span className="eb-published-hint">
                    {formData.is_published
                      ? "Post is live and visible to all readers."
                      : "Post is saved privately and not visible publicly."}
                  </span>
                </div>
              </div>
            </div>

          </div>
        );

      case "Media":
        return (
          <div className="eb-animate">
            <div className="eb-field">
              <label className="eb-label">
                <Link2 size={14} className="eb-label-icon" />
                Media URL
                <span className="eb-label-hint">— featured image or video link</span>
              </label>
              <input
                type="text"
                name="media_url"
                value={formData.media_url}
                onChange={handleInputChange}
                placeholder="https://example.com/image.jpg"
                className="eb-input"
              />
            </div>
            {formData.media_url ? (
              <div className="eb-media-preview">
                <p className="eb-media-preview-label"><Eye size={13} /> Live Preview</p>
                <img
                  src={formData.media_url}
                  alt="Media preview"
                  className="eb-media-img"
                  onError={(e) => (e.target.style.display = "none")}
                />
              </div>
            ) : (
              <div className="eb-media-empty">
                <Image size={38} className="eb-media-empty-icon" />
                <p>No media URL provided yet</p>
              </div>
            )}
          </div>
        );

      case "Excerpt":
        return (
          <div className="eb-animate">
            <div className="eb-field">
              <label className="eb-label">
                <Tag size={14} className="eb-label-icon" />
                Excerpt
                <span className="eb-label-hint">— shown in blog listings</span>
              </label>
              <input
                type="text"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                placeholder="A short one-line summary..."
                className="eb-input"
              />
            </div>
            <div className="eb-excerpt-preview">
              <p className="eb-excerpt-preview-label">Card Preview</p>
              <div className="eb-excerpt-card">
                <div className="eb-excerpt-card-thumb" />
                <div>
                  <p className="eb-excerpt-card-title">{formData.title || "Post Title"}</p>
                  <p className="eb-excerpt-card-text">
                    {formData.excerpt || "Excerpt will appear here..."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case "Publish":
        return (
          <div className="eb-animate">

            {/* Summary */}
            <div className="eb-summary">
              {[
                { key: "Title",     val: formData.title    },
                { key: "Excerpt",   val: formData.excerpt  },
                { key: "Content",   val: formData.content ? `${formData.content.length} characters` : "" },
                { key: "Media URL", val: formData.media_url },
                { key: "Post ID",   val: id, mono: true    },
              ].map(({ key, val, mono }) => (
                <div className="eb-summary-row" key={key}>
                  <span className="eb-summary-key">{key}</span>
                  <span className={`eb-summary-val ${mono ? "eb-mono" : ""}`}>
                    {val || <em className="eb-empty">Not set</em>}
                  </span>
                </div>
              ))}

              {/* ✅ is_published row in summary */}
              <div className="eb-summary-row">
                <span className="eb-summary-key">Status</span>
                <span className={`eb-summary-badge ${formData.is_published ? "published" : "draft"}`}>
                  {formData.is_published
                    ? <><Eye size={11} /> Published</>
                    : <><EyeOff size={11} /> Draft</>}
                </span>
              </div>
            </div>

            {/* Toggle */}
            <div className="eb-publish-toggle-row">
              <div>
                <h5 className="eb-publish-toggle-title">
                  {formData.is_published
                    ? <><Eye size={16} /> Currently Published</>
                    : <><EyeOff size={16} /> Currently a Draft</>}
                </h5>
                <p className="eb-publish-toggle-desc">
                  {formData.is_published
                    ? "Toggle off to unpublish and save as draft."
                    : "Toggle on to make this post live immediately after saving."}
                </p>
              </div>
              <div
                className={`eb-toggle ${formData.is_published ? "eb-toggle-on" : ""}`}
                onClick={() => setFormData((p) => ({ ...p, is_published: !p.is_published }))}
              >
                <div className="eb-toggle-thumb" />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="eb-actions-bar">
              <button
                type="button"
                className="eb-btn-secondary"
                onClick={() => navigate("/blog/list")}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="eb-btn-primary"
                disabled={isSaving || !formData.title || !formData.content}
              >
                {isSaving
                  ? <span className="eb-spinner" />
                  : <><Save size={16} /> Save Changes</>}
              </button>
            </div>

          </div>
        );

      default: return null;
    }
  };

  // ── Loading screen ────────────────────────────────────────────────────────
  if (isLoadingPost) {
    return (
      <div className="eb-page">
        <nav className="eb-navbar">
          <Link to="/"><img src={Logo} alt="Revexia" className="eb-navbar-logo" /></Link>
        </nav>
        <div className="eb-fullscreen-center">
          <span className="eb-spinner eb-spinner-lg" />
          <p>Loading post...</p>
        </div>
      </div>
    );
  }

  // ── Fetch error screen ────────────────────────────────────────────────────
  if (fetchError) {
    return (
      <div className="eb-page">
        <nav className="eb-navbar">
          <Link to="/"><img src={Logo} alt="Revexia" className="eb-navbar-logo" /></Link>
        </nav>
        <div className="eb-fullscreen-center">
          <AlertCircle size={48} color="#f87171" />
          <p style={{ color: "#f87171" }}>{fetchError}</p>
          <Link to="/blog/list" className="eb-btn-primary" style={{ textDecoration: "none" }}>
            ← Back to Blog List
          </Link>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Edit — {formData.title || "Post"} · Revexia</title>
      </Helmet>

      <div className="eb-page">

       
        

        {/* ── Layout ── */}
        <div className="eb-layout">

          {/* ── Sidebar ── */}
          <aside className="eb-sidebar">
            <div className="eb-sidebar-header">
              <div className="eb-sidebar-icon">
                <FileText size={20} />
              </div>
              <div className="eb-sidebar-header-text">
                <p className="eb-sidebar-title">Edit Post</p>
                <p className="eb-sidebar-subtitle">{formData.title || "Untitled"}</p>
              </div>
            </div>

            <nav className="eb-sidebar-nav">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`eb-nav-item ${activeSection === item.id ? "active" : ""}`}
                >
                  <div className="eb-nav-item-icon-wrap">
                    <item.icon size={17} />
                  </div>
                  <span className="eb-nav-item-label">{item.id}</span>
                  <div className={`eb-nav-dot ${
                    (item.id === "Content" && formData.title && formData.content) ||
                    (item.id === "Media"   && formData.media_url) ||
                    (item.id === "Excerpt" && formData.excerpt)
                      ? "done" : ""
                  }`} />
                  {activeSection === item.id && <div className="eb-nav-indicator" />}
                </button>
              ))}
            </nav>

            {/* Live status pill */}
            <div className={`eb-sidebar-status ${formData.is_published ? "published" : "draft"}`}>
              {formData.is_published
                ? <><Eye size={13} /> Published</>
                : <><EyeOff size={13} /> Draft</>}
            </div>

            {/* Editing badge */}
            <div className="eb-editing-badge">
              <span className="eb-editing-dot" />
              Editing existing post
            </div>
          </aside>

          {/* ── Content Panel ── */}
          <main className="eb-main">
            <form onSubmit={handleSubmit}>
              <div className="eb-panel">
                <div className="eb-panel-mesh" />

                <header className="eb-panel-header">
                  <h2 className="eb-panel-title">{activeSection} Settings</h2>
                  <div className="eb-panel-subtitle-row">
                    <div className="eb-panel-dot" />
                    <p className="eb-panel-subtitle">
                      Editing: <strong style={{ color: "#e4e6f0" }}>
                        {formData.title || "Untitled Post"}
                      </strong>
                    </p>
                  </div>
                </header>

                {error && (
                  <div className="eb-alert eb-alert-error">
                    <AlertCircle size={16} /> <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="eb-alert eb-alert-success">
                    <CheckCircle2 size={16} />
                    <span>Post updated successfully! Redirecting...</span>
                  </div>
                )}

                {renderContent()}
              </div>
            </form>
          </main>

        </div>
      </div>
    </>
  );
};

export default EditBlogPage;
